import { omit } from 'lodash';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useApiStore } from '../api/api-store';
import { PopupApi } from './popup-api';
import type { PopupState, PopupStateFunctions } from './types';

const initialState = {
  loading: {
    popups: false,
    priority: false,
  },
  popups: null,
  priorityPopup: null,
  seenPopupIds: {},
  seenDismissablePopupIds: {},
  lastPopupRefresh: null,
  lastDataFetchedFromApiAt: null,
} as const satisfies PopupState;

export const usePopupStore = create<PopupState & PopupApi & PopupStateFunctions>()(
  persist(
    (set, get) => ({
      ...initialState,
      clearPriorityPopup: () => {
        set({ priorityPopup: null } as const satisfies Partial<PopupState>);
      },
      markPopupSeen: (id) => {
        const dismissable = get().popups?.find((p) => p.id === id)?.dismissable;
        if (dismissable === false) {
          console.log('setting seen dismissable');
          set(
            (s) =>
              ({
                seenDismissablePopupIds: { ...s.seenDismissablePopupIds, [id]: true },
              }) as const satisfies Partial<PopupState>,
          );
          return;
        }

        set(
          (s) =>
            ({
              seenPopupIds: { ...s.seenPopupIds, [id]: true },
            }) as const satisfies Partial<PopupState>,
        );
      },
      popupSeen: (id) => {
        const { seenPopupIds, seenDismissablePopupIds } = get();

        return seenPopupIds[id] || seenDismissablePopupIds[id] || false;
      },
      ...PopupApi(get, set),
    }),
    {
      name: 'popup-storage',
      skipHydration: true,
      storage: createJSONStorage(() => ({
        getItem: (name: string) => {
          const store = useApiStore.getState().localStores.popup;
          return store?.getItem(name) ?? null;
        },
        setItem: (name: string, value: string) => {
          const store = useApiStore.getState().localStores.popup;
          store?.setItem(name, value);
        },
        removeItem: (name: string) => {
          const store = useApiStore.getState().localStores.popup;
          store?.removeItem(name);
        },
      })),

      partialize: (state) => omit(state, 'loading', 'seenDismissablePopupIds', 'priorityPopup'),
    },
  ),
);
